import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { App } from 'antd';
import {
  materialIssuanceService,
  MaterialIssuance,
  CreateMaterialIssuanceDTO,
} from '../services/materialIssuance.service';

export const useMaterialIssuances = (filters?: {
  startDate?: string;
  endDate?: string;
}): UseQueryResult<MaterialIssuance[], Error> => {
  return useQuery({
    queryKey: ['material-issuances', filters],
    queryFn: () => materialIssuanceService.getAll(filters),
  });
};

export const useMaterialIssuance = (id: string, options?: { enabled?: boolean }): UseQueryResult<MaterialIssuance, Error> => {
  return useQuery({
    queryKey: ['material-issuances', id],
    queryFn: () => materialIssuanceService.getById(id),
    enabled: options?.enabled !== false && !!id,
  });
};

export const useMaterialIssuancesByWorkOrder = (workOrderId: string, options?: { enabled?: boolean }): UseQueryResult<MaterialIssuance[], Error> => {
  return useQuery({
    queryKey: ['material-issuances', 'work-order', workOrderId],
    queryFn: () => materialIssuanceService.getByWorkOrder(workOrderId),
    enabled: options?.enabled !== false && !!workOrderId,
  });
};

export const useIssueMaterials = (): UseMutationResult<MaterialIssuance, Error, CreateMaterialIssuanceDTO> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (data) => materialIssuanceService.issueMaterials(data),
    onSuccess: (created, variables) => {
      queryClient.invalidateQueries({ queryKey: ['material-issuances'] });
      queryClient.invalidateQueries({ queryKey: ['material-requests'] });
      queryClient.invalidateQueries({ queryKey: ['material-requests', variables.material_request_id] });
      queryClient.invalidateQueries({ queryKey: ['items', 'stock'] });
      message.success(
        String(created?.issuance_status || '') === 'Submitted'
          ? 'Material issuance submitted for approval'
          : 'Materials issued successfully'
      );
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to issue materials');
    },
  });
};
